import i18n from "@/i18n";
import { detailOr } from "@/lib/apiErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Sparkles, Loader2, ChevronDown, Search, CheckCircle2, Circle,
    AlertCircle, X,
} from "lucide-react";

const RECIPE_CATEGORIES = [
    "Meat", "Poultry", "Fish & Seafood", "Vegetarian", "Vegan",
    "Soup", "Pasta", "Bakery", "Dessert", "Breakfast",
    "Sauce", "Side Dish", "Main Course", "Buffet",
];

// Confidence bucket → visual label & tone. Keeps the UI consistent
// with how the chef reads the number.
const confBucket = (c) => {
    if (c >= 0.95) return { label: i18n.t("ui.recipeImportDialog.strongMatch", "Strong match"), tone: "emerald" };
    if (c >= 0.85) return { label: i18n.t("ui.recipeImportDialog.likelyMatch", "Likely match"), tone: "sky" };
    if (c >= 0.72) return { label: i18n.t("ui.recipeImportDialog.possibleMatch", "Possible match"), tone: "amber" };
    return { label: i18n.t("ui.recipeImportDialog.weakMatch", "Weak match"), tone: "neutral" };
};

const toneClasses = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    neutral: "bg-neutral-50 text-neutral-700 border-neutral-200",
};

export default function RecipeImportDialog({ open, onClose, onSaved }) {
    const { user } = useApp();
    const { withRestaurant, requireSelection } = useRestaurantScope();

    // step: paste | extracting | review
    const [step, setStep] = useState("paste");
    const [text, setText] = useState("");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [category, setCategory] = useState(["Main Course"]);
    const [type, setType] = useState("local");
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setStep("paste"); setText(""); setDraft(null);
            setError(""); setCategory("Main Course"); setType("local");
        }
    }, [open]);

    const extract = async () => {
        setError("");
        if (text.trim().length < 20) {
            setError(i18n.t("ui.recipeImportDialog.pasteAtLeast20CharactersOf", "Paste at least 20 characters of recipe text."));
            return;
        }
        setStep("extracting");
        try {
            const body = withRestaurant({ text });
            const { data } = await api.post("/recipes/ai-import", body);
            // Every ingredient starts UNLINKED, even if we found strong
            // suggestions. The chef must press "Use product" to confirm.
            const ingredients = (data?.draft?.ingredients || []).map((it, idx) => ({
                _key: `imp-${idx}`,
                original_text: it.original_text || "",
                name: it.name || "",
                quantity: Number(it.quantity) || 0,
                unit: it.unit || "",
                section: it.section || "",
                matches: it.matches || [],
                selected_product: null,   // { id, name, confidence, reason }
                include_in_purchase_needs: true,
                include_in_costing: true,
            }));
            setDraft({ ...data.draft, ingredients });
            setStep("review");
        } catch (e) {
            setError(detailOr(e.response?.data?.detail, i18n.t("ui.recipeImportDialog.extractionFailedTryAgainOrUse", "Extraction failed. Try again or use New Recipe manually.")));
            setStep("paste");
        }
    };

    const updateIngredient = (key, patch) => {
        setDraft((prev) => ({
            ...prev,
            ingredients: prev.ingredients.map(
                (i) => (i._key === key ? { ...i, ...patch } : i)),
        }));
    };

    const removeIngredient = (key) => {
        setDraft((prev) => ({
            ...prev,
            ingredients: prev.ingredients.filter((i) => i._key !== key),
        }));
    };

    const stats = useMemo(() => {
        if (!draft) return { total: 0, linked: 0, unlinked: 0 };
        const total = draft.ingredients.length;
        const linked = draft.ingredients.filter(
            (i) => i.selected_product?.id).length;
        return { total, linked, unlinked: total - linked };
    }, [draft]);

    const save = async () => {
        if (!draft?.name?.trim()) {
            toast.error(i18n.t("ui.recipeImportDialog.recipeNameRequiredTheChefConfirms", "Recipe name required — the chef confirms every field before save."));
            return;
        }
        const recipeType = user?.role === "corporate" ? type : "local";
        if (recipeType === "local" && !requireSelection()) return;
        setSaving(true);
        try {
            const ingredients = draft.ingredients
                .filter((i) => (i.name || "").trim())
                .map((i) => ({
                    // When the chef confirmed a product match we use the
                    // product's canonical name as the ingredient_name so
                    // the existing costing engine (which name-matches on
                    // products.product_name) picks it up unchanged.
                    ingredient_name: i.selected_product?.name || i.name,
                    quantity: Number(i.quantity) || 0,
                    unit: i.unit || "g",
                    include_in_purchase_needs: !!i.include_in_purchase_needs,
                    include_in_costing: !!i.include_in_costing,
                    notes: i.section ? `Section: ${i.section}` : "",
                    // Smart Recipe Import metadata (Part 7 — Store).
                    original_text: i.original_text,
                    product_id: i.selected_product?.id || null,
                    match_confidence: i.selected_product?.confidence || 0,
                    user_confirmed: !!i.selected_product?.id,
                    section: i.section || "",
                }));
            let body = {
                name: draft.name.trim(),
                categories: category,
                base_yield_quantity: Number(draft.base_yield_quantity) || 1,
                base_yield_unit: draft.base_yield_unit || "portion",
                description: draft.description || "",
                instructions: draft.procedure || "",
                notes: (draft.allergens || []).length
                    ? `Allergens: ${draft.allergens.join(", ")}`
                    : "",
                ingredients,
            };
            body.recipe_type = recipeType;
            if (recipeType === "local") body = withRestaurant(body);
            await api.post("/recipes", body);
            toast.success(`Recipe imported — ${stats.linked} of ${stats.total} ingredients linked`);
            onSaved?.();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("ui.recipeImportDialog.saveFailed", "Save failed")));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="recipe-import-dialog"
                className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-amber-500" />
                        {i18n.t("ui.recipeImportDialog.importRecipeFromText", "Import Recipe from Text")}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.t("ui.recipeImportDialog.pasteAnyRecipeChatgptWordPdf", "Paste any recipe (ChatGPT, Word, PDF, email, website). The AI extracts it and suggests matches from your product catalogue. You review everything before saving.")}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto pr-1">
                    {step === "paste" && (
                        <PasteStep
                            text={text}
                            onChange={setText}
                            error={error}
                        />
                    )}
                    {step === "extracting" && (
                        <div className="py-16 flex flex-col items-center justify-center gap-3 text-neutral-600"
                             data-testid="recipe-import-loading">
                            <Loader2 className="h-8 w-8 animate-spin" />
                            <div className="text-sm font-medium">
                                {i18n.t("ui.recipeImportDialog.readingTheRecipeAndMatchingYour", "Reading the recipe and matching your products…")}
                            </div>
                            <div className="text-xs text-neutral-500">
                                {i18n.t("ui.recipeImportDialog.thisUsuallyTakes515Seconds", "This usually takes 5–15 seconds.")}
                            </div>
                        </div>
                    )}
                    {step === "review" && draft && (
                        <ReviewStep
                            draft={draft}
                            setDraft={setDraft}
                            category={category}
                            setCategory={setCategory}
                            type={type}
                            setType={setType}
                            userRole={user?.role}
                            updateIngredient={updateIngredient}
                            removeIngredient={removeIngredient}
                            stats={stats}
                        />
                    )}
                </div>

                <DialogFooter className="gap-2 border-t pt-3">
                    <button
                        data-testid="recipe-import-cancel"
                        onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold"
                    >
                        {i18n.t("common.cancel", "Cancel")}
                    </button>
                    {step === "paste" && (
                        <button
                            data-testid="recipe-import-extract"
                            onClick={extract}
                            disabled={text.trim().length < 20}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            <Sparkles className="h-4 w-4" />
                            {i18n.t("ui.recipeImportDialog.extractRecipe", "Extract Recipe")}
                        </button>
                    )}
                    {step === "review" && (
                        <button
                            data-testid="recipe-import-save"
                            onClick={save}
                            disabled={saving}
                            className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            {saving ? i18n.t("common.saving", "Saving…") : i18n.t("ui.recipeImportDialog.saveRecipe", "Save Recipe")}
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PasteStep({ text, onChange, error }) {
    return (
        <div>
            <div className="text-xs text-neutral-500 mb-2">
                {i18n.t("ui.recipeImportDialog.pasteFromAnywhereEnglishFinnishOr", "Paste from anywhere. English, Finnish, or mixed. The AI will extract name, ingredients (with quantities and units), sections and procedure.")}
            </div>
            <textarea
                data-testid="recipe-import-text"
                value={text}
                onChange={(e) => onChange(e.target.value)}
                rows={18}
                placeholder={
                    "Example:\n\n" +
                    "Venison Meatballs\n\n" +
                    "Ingredients\n" +
                    "Minced venison   120 g\n" +
                    "Cream            50 ml\n" +
                    "Onion            20 g\n" +
                    "Egg              15 g\n" +
                    "Salt             to taste\n\n" +
                    "Procedure\n" +
                    "1. Sauté the onion until translucent…"
                }
                className="w-full font-mono text-sm p-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 resize-y"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-neutral-500">
                <span>{text.length} chars</span>
                <span>{i18n.t("ui.recipeImportDialog.min20Max20000", "Min 20 · max 20 000")}</span>
            </div>
            {error && (
                <div
                    data-testid="recipe-import-error"
                    className="mt-3 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3"
                >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
}


function ReviewStep({
    draft, setDraft, category, setCategory, type, setType, userRole,
    updateIngredient, removeIngredient, stats,
}) {
    return (
        <div className="space-y-4">
            {/* Summary bar */}
            <div
                data-testid="recipe-import-summary"
                className="flex flex-wrap items-center gap-2 text-xs bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2"
            >
                <span className="font-semibold text-neutral-700">
                    {stats.total} ingredient{stats.total === 1 ? "" : "s"}
                </span>
                <span className="text-emerald-700">
                    ✓ {stats.linked} linked
                </span>
                <span className="text-amber-700">
                    ○ {stats.unlinked} unlinked
                </span>
                <span className="ml-auto text-neutral-500">
                    Review every row. Nothing saves until you click <b>{i18n.t("ui.recipeImportDialog.saveRecipe", "Save Recipe")}</b>.
                </span>
            </div>

            {/* Recipe header fields */}
            <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                    <Label>{i18n.t("ui.recipeImportDialog.recipeName", "Recipe name")}</Label>
                    <input
                        data-testid="recipe-import-name"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900"
                    />
                </div>
                <div className="col-span-2">
                    <Label>{i18n.t("ui.recipeImportDialog.categories", "Categories")}</Label>
                    <div className="text-[11px] text-neutral-500 mb-1.5">
                        Pick one or several — for example <em>{i18n.t("ui.recipeImportDialog.vegetarian", "Vegetarian")}</em> + <em>{i18n.t("ui.recipeImportDialog.buffet", "Buffet")}</em>.
                    </div>
                    <div data-testid="recipe-import-categories"
                         className="mt-1 flex flex-wrap gap-1.5">
                        {RECIPE_CATEGORIES.map((c) => {
                            const active = category.includes(c);
                            return (
                                <button
                                    type="button"
                                    key={c}
                                    onClick={() => setCategory((prev) =>
                                        prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c])}
                                    className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                                        active
                                            ? "bg-neutral-900 text-white border-neutral-900"
                                            : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                                    }`}
                                >
                                    {active && "✓ "}{i18n.t(`recipeCategories.${c.replace(/[^A-Za-z]/g, "")}`, c)}
                                </button>
                            );
                        })}
                    </div>
                </div>
                {userRole === "corporate" && (
                    <div>
                        <Label>{i18n.t("common.type", "Type")}</Label>
                        <select
                            data-testid="recipe-import-type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md"
                        >
                            <option value="corporate">{i18n.t("ui.recipeImportDialog.corporate", "Corporate")}</option>
                            <option value="local">{i18n.t("ui.recipeImportDialog.local", "Local")}</option>
                        </select>
                    </div>
                )}
                <div>
                    <Label>{i18n.t("ui.recipeImportDialog.baseYield", "Base yield")}</Label>
                    <input
                        type="number"
                        data-testid="recipe-import-yield-qty"
                        value={draft.base_yield_quantity}
                        onChange={(e) => setDraft({ ...draft, base_yield_quantity: e.target.value })}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md"
                    />
                </div>
                <div>
                    <Label>{i18n.t("ui.recipeImportDialog.yieldUnit", "Yield unit")}</Label>
                    <input
                        data-testid="recipe-import-yield-unit"
                        value={draft.base_yield_unit}
                        onChange={(e) => setDraft({ ...draft, base_yield_unit: e.target.value })}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md"
                    />
                </div>
                <div className="col-span-2">
                    <Label>{i18n.t("ui.recipeImportDialog.description", "Description")}</Label>
                    <input
                        data-testid="recipe-import-description"
                        value={draft.description || ""}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md"
                    />
                </div>
                {(draft.allergens || []).length > 0 && (
                    <div className="col-span-2">
                        <Label>{i18n.t("ui.recipeImportDialog.allergensExtracted", "Allergens (extracted)")}</Label>
                        <div className="mt-1 flex flex-wrap gap-1"
                             data-testid="recipe-import-allergens">
                            {draft.allergens.map((a, i) => (
                                <span key={i}
                                    className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                                    {a}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Ingredients */}
            <div>
                <div className="text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-2">
                    {i18n.t("ui.recipeImportDialog.ingredients", "Ingredients")}
                </div>
                <div className="space-y-2"
                     data-testid="recipe-import-ingredients">
                    {(() => {
                        // Preserve source order while grouping. If ANY row
                        // has a non-empty section we render section headers,
                        // otherwise we fall back to the flat list (spec:
                        // "do NOT attempt to guess or force grouping").
                        const rows = draft.ingredients;
                        const hasSections = rows.some(
                            (i) => (i.section || "").trim());
                        if (!hasSections) {
                            return rows.map((ing) => (
                                <IngredientRow
                                    key={ing._key}
                                    ing={ing}
                                    onChange={(patch) => updateIngredient(ing._key, patch)}
                                    onRemove={() => removeIngredient(ing._key)}
                                />
                            ));
                        }
                        // Build ordered buckets by first appearance so the
                        // chef sees the sections in the same order as the
                        // pasted source.
                        const order = [];
                        const buckets = new Map();
                        for (const ing of rows) {
                            const key = (ing.section || "").trim() || "Other";
                            if (!buckets.has(key)) {
                                buckets.set(key, []);
                                order.push(key);
                            }
                            buckets.get(key).push(ing);
                        }
                        return order.map((sec) => (
                            <div
                                key={sec}
                                data-testid={`recipe-import-section-${sec}`}
                                className="pt-1"
                            >
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 border-b border-neutral-200 pb-1 mb-2">
                                    {sec}
                                </div>
                                <div className="space-y-2">
                                    {buckets.get(sec).map((ing) => (
                                        <IngredientRow
                                            key={ing._key}
                                            ing={ing}
                                            onChange={(patch) => updateIngredient(ing._key, patch)}
                                            onRemove={() => removeIngredient(ing._key)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ));
                    })()}
                    {draft.ingredients.length === 0 && (
                        <div className="text-sm text-neutral-500 py-4 text-center">
                            {i18n.t("ui.recipeImportDialog.noIngredientsExtractedYouCanStill", "No ingredients extracted. You can still save the recipe with just a name and procedure.")}
                        </div>
                    )}
                </div>
            </div>

            {/* Procedure */}
            <div>
                <Label>{i18n.t("ui.recipeImportDialog.procedure", "Procedure")}</Label>
                <textarea
                    data-testid="recipe-import-procedure"
                    value={draft.procedure || ""}
                    onChange={(e) => setDraft({ ...draft, procedure: e.target.value })}
                    rows={8}
                    className="mt-1 w-full font-mono text-sm p-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900 resize-y"
                />
            </div>
        </div>
    );
}


function Label({ children }) {
    return (
        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
            {children}
        </label>
    );
}


function IngredientRow({ ing, onChange, onRemove }) {
    return (
        <div
            data-testid={`recipe-import-ingredient-${ing._key}`}
            className="border border-neutral-200 rounded-md p-3 bg-white"
        >
            {/* Original text (verbatim from source) */}
            {ing.original_text && (
                <div className="text-[11px] text-neutral-500 mb-2 italic truncate"
                     title={ing.original_text}>
                    From: “{ing.original_text}”
                    {ing.section && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 not-italic">
                            {ing.section}
                        </span>
                    )}
                </div>
            )}

            {/* Editable name / qty / unit */}
            <div className="grid grid-cols-12 gap-2 items-center">
                <input
                    data-testid={`ing-name-${ing._key}`}
                    value={ing.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    placeholder={i18n.t("ui.recipeImportDialog.ingredientName", "Ingredient name")}
                    className="col-span-6 h-10 px-3 border border-neutral-300 rounded-md text-sm"
                />
                <input
                    data-testid={`ing-qty-${ing._key}`}
                    type="number"
                    step="0.01"
                    value={ing.quantity}
                    onChange={(e) => onChange({ quantity: e.target.value })}
                    className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm"
                />
                <input
                    data-testid={`ing-unit-${ing._key}`}
                    value={ing.unit}
                    onChange={(e) => onChange({ unit: e.target.value })}
                    placeholder={i18n.t("ui.recipeImportDialog.unit", "unit")}
                    className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm"
                />
                <button
                    data-testid={`ing-remove-${ing._key}`}
                    onClick={onRemove}
                    className="col-span-2 h-10 rounded-md border border-neutral-300 text-neutral-600 hover:bg-neutral-50 text-xs inline-flex items-center justify-center gap-1"
                >
                    <X className="h-3 w-3" /> {i18n.t("common.remove", "Remove")}
                </button>
            </div>

            {/* Match widget */}
            <div className="mt-2">
                <MatchWidget
                    ing={ing}
                    onSelect={(prod) => onChange({ selected_product: prod })}
                    onClear={() => onChange({ selected_product: null })}
                />
            </div>
        </div>
    );
}


function MatchWidget({ ing, onSelect, onClear }) {
    const [expanded, setExpanded] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState(ing.name);
    const [searchResults, setSearchResults] = useState(null);

    // Selected/linked state — green pill with "Change" and "Unlink".
    if (ing.selected_product?.id) {
        const b = confBucket(ing.selected_product.confidence || 1);
        return (
            <div
                data-testid={`ing-linked-${ing._key}`}
                className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md border ${toneClasses.emerald}`}
            >
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-semibold">{i18n.t("ui.recipeImportDialog.linked", "Linked:")}</span>
                <span className="truncate">{ing.selected_product.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-emerald-200">
                    {Math.round((ing.selected_product.confidence || 1) * 100)}%
                    · {b.label}
                </span>
                <button
                    data-testid={`ing-change-${ing._key}`}
                    onClick={() => { setExpanded(true); onClear(); }}
                    className="ml-auto underline hover:no-underline"
                >
                    {i18n.t("ui.recipeImportDialog.change", "Change")}
                </button>
                <button
                    data-testid={`ing-unlink-${ing._key}`}
                    onClick={onClear}
                    className="underline hover:no-underline"
                >
                    {i18n.t("ui.recipeImportDialog.unlink", "Unlink")}
                </button>
            </div>
        );
    }

    // Suggestions with confidence — every one requires explicit confirmation.
    const top = ing.matches?.[0];
    const rest = ing.matches?.slice(1) || [];

    // No suggestions at all: "Unknown" (kept as-is per spec Part 3 no-match).
    if (!top) {
        return (
            <div
                data-testid={`ing-unmatched-${ing._key}`}
                className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-md border border-neutral-200 bg-neutral-50 text-neutral-700"
            >
                <Circle className="h-4 w-4" />
                <span>{i18n.t("ui.recipeImportDialog.noMatchFoundWillSaveAs", "No match found — will save as")} <b>{i18n.t("ui.recipeImportDialog.unknown", "UNKNOWN")}</b>.</span>
                <button
                    data-testid={`ing-search-${ing._key}`}
                    onClick={() => setExpanded((v) => !v)}
                    className="ml-auto underline hover:no-underline inline-flex items-center gap-1"
                >
                    <Search className="h-3 w-3" /> {i18n.t("ui.recipeImportDialog.searchProducts", "Search products")}
                </button>
                {expanded && (
                    <ProductSearch
                        ingKey={ing._key}
                        term={searchTerm}
                        setTerm={setSearchTerm}
                        results={searchResults}
                        setResults={setSearchResults}
                        searching={searching}
                        setSearching={setSearching}
                        onPick={(prod) => { onSelect(prod); setExpanded(false); }}
                    />
                )}
            </div>
        );
    }

    const bucket = confBucket(top.confidence);
    return (
        <div
            data-testid={`ing-suggested-${ing._key}`}
            className={`text-xs rounded-md border ${toneClasses[bucket.tone]}`}
        >
            <div className="flex items-center gap-2 px-2 py-1.5">
                <span className="font-semibold">{bucket.label}:</span>
                <span className="truncate flex-1">{top.product_name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/70 border border-current/20">
                    {Math.round(top.confidence * 100)}% · {top.reason}
                </span>
                <button
                    data-testid={`ing-use-${ing._key}`}
                    onClick={() => onSelect({
                        id: top.product_id,
                        name: top.product_name,
                        confidence: top.confidence,
                        reason: top.reason,
                    })}
                    disabled={!top.product_id}
                    title={!top.product_id ? i18n.t("ui.recipeImportDialog.thisSuggestionHasNoLinkableProduct", "This suggestion has no linkable product yet") : i18n.t("ui.recipeImportDialog.confirmThisProduct", "Confirm this product")}
                    className="h-7 px-2 rounded bg-neutral-900 text-white font-semibold hover:bg-neutral-800 disabled:opacity-50"
                >
                    {i18n.t("ui.recipeImportDialog.useProduct", "Use product")}
                </button>
                <button
                    data-testid={`ing-choose-${ing._key}`}
                    onClick={() => setExpanded((v) => !v)}
                    className="h-7 px-2 rounded border border-current/30 bg-white/60 font-semibold inline-flex items-center gap-1"
                >
                    Choose another <ChevronDown className="h-3 w-3" />
                </button>
                <button
                    data-testid={`ing-leave-${ing._key}`}
                    onClick={onClear}
                    className="h-7 px-2 rounded border border-current/30 bg-white/60 font-semibold"
                    title={i18n.t("ui.recipeImportDialog.leaveThisIngredientUnlinkedSavesAs", "Leave this ingredient unlinked — saves as UNKNOWN")}
                >
                    {i18n.t("ui.recipeImportDialog.leaveUnlinked", "Leave unlinked")}
                </button>
            </div>
            {expanded && (
                <div className="border-t border-current/20 bg-white p-2 space-y-1">
                    {rest.length > 0 && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">
                                {i18n.t("ui.recipeImportDialog.otherSuggestions", "Other suggestions")}
                            </div>
                            {rest.map((m) => (
                                <button
                                    key={`${m.product_id}-${m.product_name}`}
                                    data-testid={`ing-alt-${ing._key}-${m.product_id || "supplier"}`}
                                    onClick={() => {
                                        onSelect({
                                            id: m.product_id,
                                            name: m.product_name,
                                            confidence: m.confidence,
                                            reason: m.reason,
                                        });
                                        setExpanded(false);
                                    }}
                                    disabled={!m.product_id}
                                    className="w-full text-left text-xs px-2 py-1 rounded hover:bg-neutral-50 disabled:opacity-50 flex items-center gap-2"
                                >
                                    <span className="flex-1 truncate">{m.product_name}</span>
                                    <span className="text-[10px] text-neutral-500">
                                        {Math.round(m.confidence * 100)}% · {m.reason}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                    <ProductSearch
                        ingKey={ing._key}
                        term={searchTerm}
                        setTerm={setSearchTerm}
                        results={searchResults}
                        setResults={setSearchResults}
                        searching={searching}
                        setSearching={setSearching}
                        onPick={(prod) => { onSelect(prod); setExpanded(false); }}
                    />
                </div>
            )}
        </div>
    );
}


function ProductSearch({
    ingKey, term, setTerm, results, setResults,
    searching, setSearching, onPick,
}) {
    const { withRestaurant } = useRestaurantScope();
    const run = async () => {
        if (!term.trim()) return;
        setSearching(true);
        try {
            const body = withRestaurant({
                ingredient_name: term,
                limit: 10,
            });
            const { data } = await api.post(
                "/recipes/ingredients/match", body);
            setResults(data?.matches || []);
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    };
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 mt-1 mb-1">
                {i18n.t("ui.recipeImportDialog.searchCatalogue", "Search catalogue")}
            </div>
            <div className="flex items-center gap-1">
                <input
                    data-testid={`ing-search-input-${ingKey}`}
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") run(); }}
                    placeholder={i18n.t("ui.recipeImportDialog.eGVenisonTilliCream", "e.g. venison, tilli, cream…")}
                    className="flex-1 h-8 px-2 border border-neutral-300 rounded-md text-xs"
                />
                <button
                    data-testid={`ing-search-run-${ingKey}`}
                    onClick={run}
                    disabled={searching || !term.trim()}
                    className="h-8 px-2 rounded-md bg-neutral-900 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                >
                    {searching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                    {i18n.t("common.search", "Search")}
                </button>
            </div>
            {results && (
                <div className="mt-1 max-h-40 overflow-y-auto border border-neutral-200 rounded-md">
                    {results.length === 0 && (
                        <div className="text-xs text-neutral-500 px-2 py-2">
                            {i18n.t("ui.recipeImportDialog.noProductsFoundLeaveThisIngredient", "No products found — leave this ingredient unlinked (it will show as UNKNOWN in Suggested Order) or create a product later from Inventory.")}
                        </div>
                    )}
                    {results.map((m) => (
                        <button
                            key={`${m.product_id}-${m.product_name}`}
                            data-testid={`ing-result-${ingKey}-${m.product_id || "supplier"}`}
                            onClick={() => onPick({
                                id: m.product_id,
                                name: m.product_name,
                                confidence: m.confidence,
                                reason: m.reason,
                            })}
                            disabled={!m.product_id}
                            className="w-full text-left text-xs px-2 py-1 hover:bg-neutral-50 disabled:opacity-50 flex items-center gap-2 border-b border-neutral-100 last:border-0"
                        >
                            <span className="flex-1 truncate">{m.product_name}</span>
                            <span className="text-[10px] text-neutral-500">
                                {Math.round(m.confidence * 100)}% · {m.reason}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
